/**
 * Der Wrapping-Key entsperrt den lokalen Schlüsselspeicher (SECURITY.md §3.5).
 *
 * Drei Wege hinein, in dieser Reihenfolge:
 *
 *   1. **WebAuthn-PRF** — der Passkey liefert ein deterministisches Geheimnis.
 *      Der Oma-Test-taugliche Weg: Fingerabdruck auflegen, fertig, kein Passwort.
 *   2. **Passphrase** — für Geräte ohne PRF-Unterstützung.
 *   3. **Recovery-Blatt** — 12 Wörter, wenn alle Geräte weg sind (ADR-008).
 *
 * Der Wrapping-Key selbst wird nie gespeichert. Er entsteht bei jedem Entsperren
 * neu und liegt nur im Speicher, als nicht-exportierbarer `CryptoKey`.
 */
const HKDF_INFO = 'family-hub/keystore-wrapping-key/v1';
/**
 * PBKDF2 statt Argon2id, mit Begründung:
 *
 * Argon2 gibt es im Browser nur als WASM-Zusatzabhängigkeit. PBKDF2-HMAC-SHA256
 * ist in WebCrypto nativ vorhanden — kein Nachladen, läuft auch auf alten
 * Geräten. Der Preis ist die schwächere Speicherhärte gegenüber GPU-Angriffen;
 * die 600.000 Runden entsprechen der OWASP-Empfehlung für genau diesen Fall.
 *
 * Serverseitig, wo Argon2id verfügbar ist, wird es auch genutzt
 * (`modules/auth/passphrase.ts`). Diese Abweichung gilt nur für den Browser.
 */
const PBKDF2_ITERATIONS = 600_000;
/** Aus WebAuthn-PRF: das Geheimnis kommt vom Authenticator, nicht aus dem Kopf. */
export async function fromPrf(prfOutput) {
    const material = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode(HKDF_INFO),
    }, material, { name: 'AES-GCM', length: 256 }, false, // nicht exportierbar
    ['encrypt', 'decrypt']);
}
/** Aus einer Passphrase. `salt` ist gerätefest und liegt neben dem Speicher. */
export async function fromPassphrase(passphrase, salt) {
    if (passphrase.length < 12) {
        throw new Error('Die Passphrase muss mindestens 12 Zeichen haben');
    }
    const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
    return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt: salt, iterations: PBKDF2_ITERATIONS }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
/** Aus dem Recovery-Seed (siehe `recovery.ts`). */
export async function fromRecoverySeed(seed) {
    const material = await crypto.subtle.importKey('raw', seed, 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode(`${HKDF_INFO}/recovery`),
    }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
export function neuesSalz() {
    return crypto.getRandomValues(new Uint8Array(16));
}
// --- Verschlüsseln und Entschlüsseln des Speichers ---------------------------
/** AES-256-GCM. Die Nonce steht vorn, weil sie zum Ciphertext gehört. */
export async function versiegeln(key, klartext) {
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, klartext));
    const out = new Uint8Array(nonce.length + ct.length);
    out.set(nonce, 0);
    out.set(ct, nonce.length);
    return out;
}
export async function entsiegeln(key, paket) {
    if (paket.length < 13)
        throw new Error('Paket zu kurz');
    // slice statt subarray: WebCrypto verlangt einen eigenständigen Puffer, keine
    // Sicht auf einen fremden. Die Kopie ist bei Schlüsselmaterial vernachlässigbar.
    const nonce = paket.slice(0, 12);
    const ct = paket.slice(12);
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, ct));
}
//# sourceMappingURL=wrapping-key.js.map